/**
 * Mini-games soundscape — synthesized WebAudio (same idiom as chime.ts /
 * dadlinkSounds.ts): no audio assets, nothing to 404 on the kiosk, and it can
 * never throw (sound is garnish, not function). One instance per game
 * component, created lazily on first use, dispose() on unmount.
 *
 *   point()  — short bright blip, one point scored
 *   jump()   — quick upward whoosh (Runner)
 *   hit()    — dull thud, obstacle/bomb touched, life lost
 *   flip()   — soft tick, card turned (Memory)
 *   match()  — two-note chime, pair found (Memory)
 *   win()    — rising 3-note fanfare
 *   lose()   — falling 2-note tone
 */
export type GameAudio = ReturnType<typeof createGameAudio>;

export function createGameAudio() {
  let ctx: AudioContext | null = null;

  const ac = (): AudioContext | null => {
    try {
      if (!ctx) ctx = new AudioContext();
      ctx.resume().catch(() => {});
      return ctx;
    } catch {
      return null;
    }
  };
  const nowT = () => {
    const c = ac();
    return c ? c.currentTime + 0.015 : 0;
  };
  const tone = (
    freq: number,
    t0: number,
    dur: number,
    gain: number,
    type: OscillatorType = "sine",
    sweepTo?: number
  ) => {
    const c = ac();
    if (!c || !t0) return;
    try {
      const o = c.createOscillator();
      const g = c.createGain();
      o.type = type;
      o.frequency.setValueAtTime(freq, t0);
      if (sweepTo) o.frequency.exponentialRampToValueAtTime(sweepTo, t0 + dur);
      g.gain.setValueAtTime(0, t0);
      g.gain.linearRampToValueAtTime(gain, t0 + 0.015);
      g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
      o.connect(g);
      g.connect(c.destination);
      o.start(t0);
      o.stop(t0 + dur + 0.05);
    } catch {
      /* never let sound break the call flow */
    }
  };

  return {
    point() {
      const t0 = nowT();
      if (!t0) return;
      tone(1046.5, t0, 0.14, 0.13, "square");
    },
    jump() {
      const t0 = nowT();
      if (!t0) return;
      tone(320, t0, 0.16, 0.09, "sine", 640);
    },
    hit() {
      const t0 = nowT();
      if (!t0) return;
      tone(160, t0, 0.24, 0.16, "sawtooth", 60);
    },
    flip() {
      const t0 = nowT();
      if (!t0) return;
      tone(700, t0, 0.07, 0.06, "triangle");
    },
    match() {
      const t0 = nowT();
      if (!t0) return;
      tone(783.99, t0, 0.16, 0.13);
      tone(1174.66, t0 + 0.1, 0.22, 0.13);
    },
    win() {
      const t0 = nowT();
      if (!t0) return;
      [880, 1174.66, 1567.98].forEach((f, i) => tone(f, t0 + i * 0.1, 0.35, 0.15));
    },
    lose() {
      const t0 = nowT();
      if (!t0) return;
      tone(392, t0, 0.4, 0.13, "sine", 220);
      tone(196, t0 + 0.32, 0.6, 0.09);
    },
    dispose() {
      try {
        ctx?.close();
      } catch {
        /* ignore */
      }
      ctx = null;
    },
  };
}
